/**
 * Lightweight, dependency-free i18n for the student app.
 *
 * - DE is the default locale for students.
 * - `locale` is a Svelte writable store so components react to changes.
 * - `t(key, params?)` resolves a flat dotted key against the active dictionary,
 *   falling back to DE, then to the key itself. Supports `{name}` placeholder
 *   interpolation (e.g. the raise-hand countdown's `{count}`).
 */
import { derived, get, writable, type Readable, type Writable } from "svelte/store";
import type { Locale } from "@diy-avatar/shared/api";

export const DEFAULT_LOCALE: Locale = "de";

const SUPPORTED: readonly Locale[] = ["de", "en"] as const;

export type Dict = Record<string, string>;

/** German — student-friendly, natural phrasing. Default locale. */
const de: Dict = {
  // App / boot
  "boot.loading": "Lädt…",

  // Login
  "login.title": "wiederdabei",

  "login.subtitle": "Melde dich an, um deiner Klasse beizutreten.",

  "login.username": "Benutzername",

  "login.password": "Passwort",

  "login.signIn": "Anmelden",

  "login.signingIn": "Anmelden…",

  "login.rememberMe": "Angemeldet bleiben",

  "login.rememberMeHint": "Nur auf deinem eigenen Gerät nutzen.",

  "login.error.required": "Benutzername und Passwort sind nötig.",

  "login.error.invalid": "Falscher Benutzername oder Passwort.",

  "login.error.failed": "Anmeldung fehlgeschlagen.",

  "login.forgot": "Passwort vergessen?",

  "login.notVerified": "Deine E-Mail ist noch nicht bestätigt.",

  "login.resend": "Bestätigungs-E-Mail erneut senden",

  "login.resending": "Senden…",

  "login.resent": "Wenn nötig, ist eine neue E-Mail unterwegs.",

  // Concurrent-session limit (HTTP 409 session_limit)
  "login.sessionLimit.title": "Zu viele aktive Anmeldungen",

  "login.sessionLimit.intro":
    "Du kannst auf höchstens {max} Geräten gleichzeitig angemeldet sein. Wähle eine oder mehrere bestehende Anmeldungen zum Beenden aus, um hier weiterzumachen.",

  "login.sessionLimit.selectHint":
    "Wähle mindestens {count} Anmeldung(en) aus, um einen Platz frei zu machen.",

  "login.sessionLimit.activeFor": "seit {age} aktiv",

  "login.sessionLimit.justNow": "gerade eben",

  "login.sessionLimit.unknownAge": "unbekannt",

  "login.sessionLimit.unknownDevice": "unbekanntes Gerät",

  "login.sessionLimit.unknownBrowser": "unbekannter Browser",

  "login.sessionLimit.unknownIp": "unbekannte IP",

  "login.sessionLimit.killContinue": "Ausgewählte beenden & weiter",

  "login.sessionLimit.cancel": "Abbrechen",

  "account.back": "← Zurück zur Anmeldung",

  "reset.title": "Passwort vergessen",

  "reset.help": "Gib deine E-Mail ein. Wir senden dir einen Link.",

  "reset.email": "E-Mail",

  "reset.send": "Link senden",

  "reset.sending": "Senden…",

  "reset.sent": "Wenn ein Konto existiert, ist ein Link unterwegs. Schau in dein Postfach.",

  "reset.newTitle": "Neues Passwort setzen",

  "reset.newPassword": "Neues Passwort",

  "reset.confirm": "Passwort bestätigen",

  "reset.save": "Passwort speichern",

  "reset.saving": "Speichern…",

  "reset.done": "Dein Passwort wurde geändert. Weiterleitung…",

  "reset.mismatch": "Die Passwörter stimmen nicht überein.",

  "reset.invalid": "Dieser Link ist ungültig oder abgelaufen.",

  "reset.e2eeWarn":
    "Achtung: Dieser Reset kann deinen bestehenden Verschlüsselungs-Schlüssel NICHT wiederherstellen (dein altes Passwort ist unbekannt). Du brauchst deinen Wiederherstellungscode – sonst bekommst du einen neuen Schlüssel und musst ihn neu hinzufügen.",

  "reset.recoveryTitle": "Verschlüsselung wiederherstellen (optional)",

  "reset.recoveryInfo":
    "Hast du einen Wiederherstellungscode? Gib ihn ein, um deinen Verschlüsselungs-Schlüssel zu retten.",

  "reset.recoveryCode": "Wiederherstellungscode",

  "reset.recoverySubmit": "Schlüssel wiederherstellen",

  "reset.recoveryOk": "Verschlüsselung wiederhergestellt.",

  "reset.recoveryFail": "Wiederherstellung fehlgeschlagen. Prüfe den Code.",

  "verify.title": "E-Mail-Bestätigung",

  "verify.loading": "Deine E-Mail wird bestätigt…",

  "verify.ok": "Deine E-Mail ist bestätigt. Du kannst dich jetzt anmelden.",

  "verify.fail": "Dieser Link ist ungültig oder abgelaufen.",

  // Class — header / status
  "class.logout": "Abmelden",

  "class.leaveRoom": "Raum verlassen",

  // Class — mobile header hamburger (collapses the secondary header actions)
  "class.menu.open": "Weitere Aktionen öffnen",

  "class.menu.close": "Weitere Aktionen schließen",

  // Class — clean-view toggle (hover tooltips)
  "cleanView.show.tip": "Bedienelemente wieder einblenden.",

  "cleanView.hide.tip": "Alle Bedienelemente ausblenden – nur der Stream bleibt.",

  // Room selector (front-door gate)
  "room.finding": "Deine Klassenräume werden gesucht…",

  "room.pick": "Klassenraum wählen",

  "room.none": "Gerade ist kein Klassenraum online.",

  "room.retry": "Erneut versuchen",

  "room.visibility.public": "Öffentlich",

  "room.visibility.restricted": "Eingeschränkt",

  // E2EE settings popup (opened from the HUD lock)
  "e2ee.settings.title": "Verschlüsselung",

  "e2ee.settings.statusEncrypted": "Dein Stream ist verschlüsselt.",

  "e2ee.settings.statusPlain": "Dein Stream ist nicht verschlüsselt.",

  "e2ee.settings.statusForced":
    "Verschlüsselung ist durch das Klassengerät vorgeschrieben.",

  "e2ee.settings.statusRekey":
    "Der Verschlüsselungsschlüssel hat sich geändert – QR-Code neu scannen.",

  "e2ee.settings.statusUnavailable":
    "Verschlüsselung wird von diesem Browser nicht unterstützt.",

  "e2ee.settings.enable": "Verschlüsselung einschalten",

  "e2ee.settings.enabling": "Verschlüsselung wird aktiviert…",
  "e2ee.settings.enabledToast": "Verbindung ist jetzt verschlüsselt",

  "e2ee.settings.disable": "Verschlüsselung ausschalten",

  "e2ee.settings.scan": "QR-Code scannen",

  "e2ee.settings.repair": "Verschlüsselung reparieren",

  "e2ee.settings.close": "Schließen",

  "status.connected": "Verbunden",

  "status.connecting": "Verbinde…",

  "status.disconnected": "Getrennt",

  "status.error": "Fehler",

  // Class — overlays
  "raisehand.stop": "Melden beenden",

  "clock.label": "Aktuelle Uhrzeit",

  "vu.label": "Lautstärke der Klasse",

  "camera.label": "Kamera wählen",

  "selfPreview.label": "Deine Kamera (live für die Klasse)",

  "selfPreview.tag": "Live",

  "adminMsg.dismiss": "Nachricht schließen",

  // Class — device offline banner
  "device.offline.title": "Gerät ist nicht online",

  "device.offline.lastOnline": "Zuletzt online",

  "device.offline.offlineFor": "Offline seit",

  "device.offline.wasOnline": "War online für",

  // Class — controls
  "mode.goVisible": "Sichtbar werden",

  "mode.goInvisible": "Unsichtbar werden",

  "raisehand.start": "Melden",

  "audio.on": "Mikro an",

  "audio.off": "Mikro aus",

  "video.on": "Kamera an",

  "video.off": "Kamera aus",

  "sound.on": "Ton an",

  "sound.off": "Ton aus",

  "text.open": "Text",

  // Class — control tooltips (describe the result of the action)
  "mode.goInvisible.tip": "Niemand in der Klasse sieht oder hört dich mehr.",

  "mode.goVisible.tip": "Deine Klasse kann dich wieder sehen und hören.",

  "raisehand.start.tip": "Du meldest dich – die Lehrerin sieht, dass du etwas sagen möchtest.",

  "raisehand.stop.tip": "Du nimmst deine Meldung zurück.",

  "audio.on.tip": "Schalte dein Mikro an, damit dich die Klasse hört.",

  "audio.off.tip": "Schalte dein Mikro aus – niemand hört dich.",

  "video.on.tip": "Schalte deine Kamera an, damit dich die Klasse sieht.",

  "video.off.tip": "Schalte deine Kamera aus – niemand sieht dich.",

  "sound.on.tip": "Stelle die Klasse wieder an – du hörst sie.",

  "sound.off.tip": "Stelle die Klasse stumm – du hörst sie nicht mehr.",

  "text.open.tip": "Schreibe eine Nachricht an die Klasse.",

  // Class — text overlay
  "text.placeholder": "Schreib eine Nachricht…",

  "text.send": "Senden",

  "text.close": "Schließen",

  // Class — video confirm dialog
  "video.confirm.title": "Kamera an die Klasse senden?",

  "video.confirm.starting": "Kamera startet…",

  "video.confirm.preview": "Das sieht deine Klasse.",

  "video.confirm.cancel": "Abbrechen",

  "video.confirm.send": "An die Klasse senden",

  // Class — errors
  "error.sessionExpired": "Sitzung abgelaufen. Bitte melde dich neu an.",

  "error.joinFailed": "Konnte der Klasse nicht beitreten.",

  // Class — connect errors (classified). Each message must be understandable
  // without technical knowledge AND say what to try next. The stable code
  // (ICE_FAILED etc.) is rendered separately so it can be quoted to support.
  "error.connect.roomOffline":
    "Die Klasse ist gerade nicht online. Warte kurz und versuche es noch einmal – oder frag deine Lehrkraft, ob das Gerät im Klassenzimmer eingeschaltet ist.",

  "error.connect.sessionLimit":
    "Gerade sind zu viele Geräte in dieser Klasse. Schließe die App auf anderen Geräten und versuche es noch einmal.",

  "error.connect.serverBusy":
    "Die Klasse ist gerade belegt. Warte einen Moment und versuche es noch einmal.",

  "error.connect.serverError":
    "Der Server hat gerade ein Problem. Bitte versuche es in ein paar Minuten noch einmal.",

  "error.connect.signalUnreachable":
    "Der Server ist nicht erreichbar. Prüfe deine Internetverbindung und versuche es noch einmal.",

  "error.connect.authExpired":
    "Deine Anmeldung ist abgelaufen. Bitte melde dich neu an.",

  "error.connect.e2eeFailed":
    "Die Verschlüsselung konnte nicht eingerichtet werden. Scanne den QR-Code aus dem Klassenzimmer neu oder repariere die Verschlüsselung in den Einstellungen.",

  "error.connect.iceFailed":
    "Die Verbindung wird von deinem Netzwerk blockiert. Probiere ein anderes WLAN oder dein Mobilfunknetz.",

  "error.connect.turnUnreachable":
    "Es konnte keine Medienverbindung aufgebaut werden. Probiere ein anderes WLAN oder dein Mobilfunknetz – oft blockiert ein Schul- oder Firmennetz die Verbindung.",

  "error.connect.networkOffline":
    "Du bist offline. Prüfe dein WLAN oder Mobilfunk und versuche es noch einmal.",

  "error.connect.unknown":
    "Die Verbindung hat nicht geklappt. Versuche es noch einmal – wenn es wieder nicht geht, nenne den Code unten beim Support.",

  // Short labels for the status bar (replace the bare "Fehler").
  "error.connect.short.server_error": "Server-Problem",

  "error.connect.short.auth_expired": "Abgemeldet",

  "error.connect.short.e2ee_failed": "Verschlüsselung",

  "error.connect.short.ice_failed": "Netzwerk blockiert",

  "error.connect.short.turn_unreachable": "Netzwerk blockiert",

  "error.connect.short.network_offline": "Offline",

  "error.connect.short.unknown": "Fehler",

  /** Rendered as the small secondary line: "Code: ICE_FAILED". */
  "error.connect.code": "Code: {code}",

  "error.connect.detailsShow": "Technische Details",

  "error.cameraBlocked": "Kamera blockiert",

  "error.mic": "Mikro: {reason}",

  "error.cam": "Kamera: {reason}",

  "error.blocked": "blockiert",

  // Install prompt (iOS "Add to Home Screen")
  "install.title": "App aufs Handy holen",

  "install.step1": "1. Tippe auf „Teilen“ (das Quadrat mit dem Pfeil nach oben).",

  "install.step2": "2. Wähle „Zum Home-Bildschirm“.",

  "install.gotIt": "Verstanden",

  "install.dismiss": "Schließen",

  // Profile image prompt / upload
  "profile.title": "Dein Profilbild",

  "profile.help": "Wähle ein Foto. Deine Klasse sieht es, wenn deine Kamera aus ist.",

  "profile.choose": "Foto auswählen",

  "profile.retake": "Anderes Foto",

  "profile.save": "Speichern",

  "profile.saving": "Speichern…",

  "profile.skip": "Später",

  "profile.error": "Das Bild konnte nicht gespeichert werden. Versuch es nochmal.",

  "profile.tooLarge": "Das Bild ist zu groß. Wähle ein kleineres Foto.",

  "profile.placeholderAlt": "Dein Profilbild",

  "profile.takePhoto": "Foto aufnehmen",

  "webcam.title": "Foto aufnehmen",

  "webcam.snapshot": "Aufnehmen",

  "webcam.use": "Foto verwenden",

  "webcam.retry": "Neu aufnehmen",

  "webcam.cancel": "Abbrechen",

  "webcam.error": "Kamera nicht verfügbar. Wähle stattdessen ein Foto.",

  "viewers.label": "Wer schaut zu",

  // E2EE (Phase 5 — Scaffolding)
  "e2ee.status.encrypted": "Verschlüsselt",

  "e2ee.status.partial": "Teilweise verschlüsselt",

  "e2ee.status.unencrypted": "Nicht verschlüsselt",

  "e2ee.scan.title": "Geräteschlüssel scannen",

  "e2ee.qr.scanRequired":
    "QR-Scan erforderlich – scanne den Code am Klassengerät, um teilzunehmen.",

  "e2ee.scan.help": "Scanne den QR-Code im Klassengerät, um den Schlüssel zu speichern.",

  "e2ee.scan.button": "Schlüssel scannen",

  "e2ee.scan.start": "Kamera starten",

  "e2ee.scan.stop": "Stopp",

  "e2ee.scan.close": "Schließen",

  "e2ee.scan.cameraError": "Kamera konnte nicht gestartet werden.",

  "e2ee.scan.error":
    "QR-Code konnte nicht verarbeitet werden. Bitte erneut versuchen.",

  "e2ee.scan.invalid": "Ungültiger QR-Code.",

  "e2ee.scan.stored": "Schlüssel gespeichert.",

  "e2ee.scan.storedNotLive": "Schlüssel gespeichert, aber Live-Entschlüsselung konnte nicht aktiviert werden.",

  "e2ee.scan.storeError": "Schlüssel konnte nicht gespeichert werden.",

  "e2ee.scan.expired": "QR-Code ist abgelaufen. Bitte neu erzeugen.",

  "e2ee.scan.badSig": "Signatur ungültig – QR-Code nicht vertrauenswürdig.",

  "e2ee.scan.noIdentity":
    "Verschlüsselung noch nicht eingerichtet. Bitte neu anmelden.",

  // E2EE key provisioning from the Profile page (Part B: scan / paste)
  "e2ee.manual.title": "Schlüssel hinzufügen",
  "e2ee.manual.info":
    "Scanne den QR-Code des Klassengeräts oder füge den Schlüssel als Text ein, um ihn deinem Konto hinzuzufügen.",
  "e2ee.manual.addKey": "Schlüssel hinzufügen",
  "e2ee.manual.pasteLabel": "Schlüssel einfügen",
  "e2ee.manual.placeholder": "QR-Inhalt oder Schlüssel hier einfügen…",
  "e2ee.manual.submit": "Schlüssel speichern",
  "e2ee.manual.label": "Code vom Klassengerät eingeben",
  "e2ee.manual.or": "oder Code manuell eingeben",
  "e2ee.manual.toggle": "Code eingeben statt scannen",
  "e2ee.manual.scanToggle": "Stattdessen QR-Code scannen",

  // E2EE overlay lock (Phase 5) — click to toggle / locked / unavailable states
  "e2ee.overlay.encryptedToggle":
    "Verschlüsselt – tippen, um die Verschlüsselung auszuschalten.",
  "e2ee.overlay.plainToggle":
    "Nicht verschlüsselt – tippen, um die Verschlüsselung einzuschalten.",
  "e2ee.overlay.forced":
    "Verschlüsselung ist durch das Klassengerät vorgeschrieben.",
  "e2ee.overlay.unavailable":
    "Verschlüsselung wird von diesem Browser nicht unterstützt.",
  // Key rotation (Phase 6): the active session key changed → re-scan needed.
  "e2ee.overlay.rekey":
    "Der Verschlüsselungsschlüssel hat sich geändert – QR-Code neu scannen.",
  "e2ee.rekey.title": "Verschlüsselungsschlüssel geändert",
  "e2ee.rekey.detail":
    "Das Klassengerät verwendet einen neuen Schlüssel. Scanne den QR-Code erneut, um die verschlüsselte Übertragung fortzusetzen.",
  "e2ee.help.button": "Hilfe",
  "e2ee.help.title": "Verschlüsselung – Hilfe",
  "e2ee.help.current": "Deine Situation gerade:",
  "e2ee.help.intro":
    "Bei Ende-zu-Ende-Verschlüsselung werden Bild und Ton schon auf dem Klassengerät verschlüsselt und erst auf deinem Gerät wieder entschlüsselt. Der Server sieht nur verschlüsselte Daten und kann nicht mitlesen oder mithören.",
  "e2ee.help.status.title": "Das Schloss-Symbol",
  "e2ee.help.status.body":
    "Grün = verschlüsselt, Gelb = nicht verschlüsselt, Gelb-Orange = teilweise verschlüsselt.",
  "e2ee.help.optional.title": "Verschlüsselung ist optional",
  "e2ee.help.optional.body":
    "Du kannst die Verschlüsselung über das Schloss-Symbol selbst ein- oder ausschalten.",
  "e2ee.help.forced.title": "Verschlüsselung ist vorgeschrieben",
  "e2ee.help.forced.body":
    "Das Klassengerät verlangt Verschlüsselung, deshalb kannst du sie nicht ausschalten. Falls du dazu aufgefordert wirst, scanne den QR-Code am Klassengerät.",
  "e2ee.help.qr.title": "QR-Scan erforderlich",
  "e2ee.help.qr.body":
    "Die automatische Schlüsselverteilung ist abgeschaltet. Du musst den Schlüssel persönlich übernehmen: Scanne den QR-Code, der am Klassengerät angezeigt wird.",
  "e2ee.help.rekey.title": "Der Schlüssel hat sich geändert",
  "e2ee.help.rekey.body":
    "Das Klassengerät verwendet einen neuen Schlüssel. Tippe auf „Schlüssel scannen“ und scanne den aktuellen QR-Code am Klassengerät erneut.",
  "e2ee.help.unsupported.title": "Dieser Browser unterstützt keine Verschlüsselung",
  "e2ee.help.unsupported.body":
    "Verwende einen aktuellen Chrome- oder Chromium-Browser am Desktop. iPhone und iPad werden nicht unterstützt.",
  "e2ee.help.blocked.title": "Beitritt blockiert",
  "e2ee.help.blocked.body":
    "Dieser Raum verlangt Verschlüsselung, aber dein Browser unterstützt sie nicht. Wechsle zu einem aktuellen Desktop-Chrome und versuche es erneut.",
  "e2ee.help.close": "Schließen",
  // Blocked-join remediation screen (forced E2EE + incapable client)
  "e2ee.blocked.title": "Verschlüsselung erforderlich",
  "e2ee.blocked.detail":
    "Dieser Klassenraum verlangt Ende-zu-Ende-Verschlüsselung, aber dein Browser unterstützt sie nicht. Die Sitzung kann daher nicht gestartet werden.",
  "e2ee.blocked.step.chrome":
    "Verwende einen aktuellen Chrome- oder Chromium-Desktop-Browser.",
  "e2ee.blocked.step.ios":
    "iOS-Geräte (iPhone/iPad) werden nicht unterstützt.",
  "e2ee.blocked.step.update":
    "Aktualisiere deinen Browser auf die neueste Version und versuche es erneut.",
  "e2ee.blocked.retry": "Erneut versuchen",
  // First-run scan wording (P1): distinct from rotation ("key changed").
  "e2ee.scan.firstRun.title": "Verschlüsselung aktivieren",
  "e2ee.scan.firstRun.body":
    "Scanne den QR-Code am Klassen-Display, um die Ende-zu-Ende-Verschlüsselung zu starten.",

  // Recovery-code routing (Phase 1): login could not unlock the stored identity
  // but a recovery blob exists — a QR scan can't help, so ask for the code.
  "e2ee.recovery.title": "Verschlüsselung wiederherstellen",
  "e2ee.recovery.detail":
    "Dein Verschlüsselungs-Schlüssel konnte nach einer Passwortänderung nicht entsperrt werden. Gib deinen Wiederherstellungscode ein, um wieder Zugriff zu bekommen.",
  "e2ee.recovery.placeholder": "Wiederherstellungscode",
  "e2ee.recovery.submit": "Wiederherstellen",
  "e2ee.recovery.ok": "Verschlüsselung wiederhergestellt.",
  "e2ee.recovery.fail": "Wiederherstellung fehlgeschlagen. Prüfe den Code.",

  // Compatibility downgrade (Phase 3): the whole room runs in plaintext because
  // a participant's device cannot encrypt.
  "e2ee.compat.plaintextTitle": "Verschlüsselung AUS",
  "e2ee.compat.plaintextDetail":
    "Ein Teilnehmergerät kann nicht verschlüsseln – diese Sitzung läuft unverschlüsselt.",
  // User-controlled / automatic plaintext fallback (compat downgrade).
  "e2ee.compat.continuePlain": "Unverschlüsselt fortfahren",
  // Reliability: identity not ready yet (login provisioning failed/timed out).
  "e2ee.preparing": "Verschlüsselung wird vorbereitet…",
  "e2ee.notReady.title": "Verschlüsselung noch nicht bereit",
  "e2ee.notReady.detail":
    "Dein Verschlüsselungs-Schlüssel ist noch nicht verfügbar. Bitte einen Moment warten – oder unverschlüsselt fortfahren.",
  // FIX B: bounded notReady auto-recovery — live countdown + terminal + manual.
  "e2ee.notReady.recovering":
    "Verschlüsselung wird eingerichtet … noch {seconds}s. Du musst nichts tun.",
  "e2ee.notReady.terminal":
    "Verschlüsselung konnte nicht automatisch eingerichtet werden. Tippe auf „Schlüssel erneuern“ oder melde dich neu an.",
  "e2ee.notReady.reprovision": "Schlüssel erneuern / Neu verbinden",
  // Keyless recovery UX: key from the classroom device hasn't arrived yet.
  "e2ee.keyMissing.title": "Verschlüsselung wird eingerichtet",
  "e2ee.keyMissing.detail":
    "Dein Schlüssel vom Klassengerät fehlt noch. Das Video bleibt verschlüsselt, bis der Schlüssel da ist.",
  "e2ee.keyMissing.waiting":
    "Warte auf den Schlüssel vom Klassengerät … noch {seconds}s",
  "e2ee.keyMissing.waitingHint":
    "Das kann bis zu einer Minute dauern. Du musst nichts tun.",
  "e2ee.keyMissing.repair": "Verschlüsselung reparieren",
  "e2ee.keyMissing.relogin": "Neu anmelden, um Verschlüsselung zu reparieren",
  "e2ee.keyMissing.reloginDetail":
    "Melde dich kurz neu an. Danach kommst du automatisch zurück in den Klassenraum.",

  // Class — teacher pause overlay
  "paused.title": "Dein(e) LehrerIn hat den Stream pausiert und macht gleich weiter.",

  "video.confirm.camera": "Kamera",

  // Demo / Test-Modus
  "testMode.badge": "Test-Modus",

  "testMode.tip": "Du bist im Test-Modus. Dies ist eine Demo – es wird nichts aufgezeichnet.",

  "demo.signingIn": "Demo wird gestartet…",

  "demo.failed": "Demo konnte nicht gestartet werden.",

  "demo.placeholder": "Beispiel-Stream (Test-Modus)",

  "demo.classScreen.title": "Klassen-Ansicht · das sieht die Klasse",
  "demo.classScreen.handUp": "{name} meldet sich",
  "demo.classScreen.you": "Du",

  // Profile page
  "profilePage.open": "Profil öffnen",
  "profilePage.back": "← Zurück zur Klasse",
  "profilePage.title": "Dein Profil",
  "profilePage.saved": "Gespeichert.",
  "profilePage.saveError": "Konnte nicht gespeichert werden. Versuch es nochmal.",
  "profilePage.required": "Bitte alle Felder ausfüllen.",
  "profilePage.wrongPassword": "Das Passwort ist falsch.",
  "profilePage.currentPassword": "Aktuelles Passwort",

  "profilePage.photo.title": "Profilbild",
  "profilePage.photo.remove": "Bild entfernen",
  "profilePage.photoRemoved": "Bild entfernt.",

  "profilePage.identity.title": "Name & Spitzname",
  "profilePage.identity.fullName": "Voller Name",
  "profilePage.identity.nickname": "Spitzname",
  "profilePage.identity.nicknameHint":
    "Dein Spitzname wird statt deiner E-Mail angezeigt.",
  "profilePage.identity.language": "Sprache",

  "profilePage.email.title": "E-Mail-Adresse",
  "profilePage.email.current": "Aktuell",
  "profilePage.email.new": "Neue E-Mail",
  "profilePage.email.submit": "E-Mail ändern",
  "profilePage.email.sent":
    "Wir haben einen Bestätigungslink an die neue Adresse gesendet.",
  "profilePage.email.pending":
    "Bestätigung ausstehend: {email}. Prüfe dein Postfach.",
  "profilePage.email.taken": "Diese E-Mail wird bereits verwendet.",
  "profilePage.email.invalid": "Diese E-Mail-Adresse ist ungültig.",

  "profilePage.password.title": "Passwort",
  "profilePage.password.new": "Neues Passwort",
  "profilePage.password.confirm": "Neues Passwort bestätigen",
  "profilePage.password.submit": "Passwort ändern",
  "profilePage.password.changed": "Dein Passwort wurde geändert.",
  "profilePage.password.changedRewrapFailed":
    "Passwort geändert, aber dein Verschlüsselungs-Schlüssel konnte nicht übernommen werden. Stelle ihn unten mit deinem Wiederherstellungscode wieder her.",
  "profilePage.password.needRecovery":
    "Diese Passwortänderung würde deinen Verschlüsselungs-Schlüssel zerstören. Erstelle zuerst unten einen Wiederherstellungscode.",
  "profilePage.password.confirmTitle": "Passwort wirklich ändern?",
  "profilePage.password.confirmBody":
    "Beim Ändern deines Passworts wird dein Verschlüsselungs-Schlüssel neu gesichert. Falls etwas schiefgeht, brauchst du deinen Wiederherstellungscode.",
  "profilePage.password.confirmNoRecovery":
    "Du hast keinen Wiederherstellungscode. Geht etwas schief, kann dein Schlüssel nicht wiederhergestellt werden.",
  "profilePage.password.confirmCancel": "Abbrechen",
  "profilePage.password.confirmProceed": "Passwort ändern",
  "profilePage.password.mismatch": "Die Passwörter stimmen nicht überein.",
  "profilePage.password.weak": "Das Passwort muss mindestens 8 Zeichen haben.",

  "profilePage.e2ee.title": "Verschlüsselung",
  "profilePage.e2ee.info":
    "Ende-zu-Ende-Verschlüsselung schützt deinen Stream. Nur du und deine Klasse können ihn sehen.",
  "profilePage.e2ee.identity": "Dein Schlüssel",
  "profilePage.e2ee.noIdentity": "Noch nicht eingerichtet",
  "profilePage.e2ee.recoveryNeeded":
    "Deine Verschlüsselung konnte nach einer Passwortänderung nicht entsperrt werden. Stelle sie mit deinem Wiederherstellungscode wieder her.",
  "profilePage.e2ee.rewrapError":
    "Bei der letzten Passwortänderung konnte dein Verschlüsselungs-Schlüssel nicht übernommen werden. Stelle ihn mit deinem Wiederherstellungscode wieder her oder füge den Schlüssel erneut hinzu.",
  "profilePage.e2ee.recoveryTitle": "Wiederherstellungscode",
  "profilePage.e2ee.recoveryInfo":
    "Mit dem Wiederherstellungscode bekommst du nach einer Passwortänderung wieder Zugriff auf deine Verschlüsselung.",
  "profilePage.e2ee.recoveryWarn":
    "Schreibe diesen Code auf und bewahre ihn sicher auf. Er wird nur einmal angezeigt.",
  "profilePage.e2ee.recoverySetup": "Wiederherstellungscode erstellen",
  "profilePage.e2ee.recoveryRotate": "Neuen Code erstellen",
  "profilePage.e2ee.recoveryDisable": "Wiederherstellung deaktivieren",
  "profilePage.e2ee.recoverySaved": "Wiederherstellungscode erstellt.",
  "profilePage.e2ee.recoveryDisabled": "Wiederherstellung deaktiviert.",
  "profilePage.e2ee.recoveryError": "Konnte nicht eingerichtet werden.",
  "profilePage.e2ee.restoreTitle": "Verschlüsselung wiederherstellen",
  "profilePage.e2ee.restoreInfo":
    "Gib deinen Wiederherstellungscode und dein aktuelles Passwort ein, um deine Verschlüsselung wiederherzustellen.",
  "profilePage.e2ee.restoreCode": "Wiederherstellungscode",
  "profilePage.e2ee.restoreSubmit": "Wiederherstellen",
  "profilePage.e2ee.restored": "Verschlüsselung wiederhergestellt.",
  "profilePage.e2ee.restoreFailed":
    "Wiederherstellung fehlgeschlagen. Prüfe Code und Passwort.",

  "profilePage.teacher.title": "Kontakt zur Lehrkraft",
  "profilePage.teacher.info": "E-Mail-Adresse deiner Lehrkraft.",
  "profilePage.teacher.forRoom": "Lehrkraft für „{room}“.",
  "profilePage.teacher.none": "Keine Lehrkraft-Kontaktadresse verfügbar.",

  "confirmEmail.title": "E-Mail-Änderung bestätigen",
  "confirmEmail.loading": "Deine neue E-Mail wird bestätigt…",
  "confirmEmail.ok": "Deine E-Mail-Adresse wurde geändert.",
  "confirmEmail.fail": "Dieser Link ist ungültig oder abgelaufen.",

  // Forced first-login password change
  "changePw.title": "Neues Passwort festlegen",

  "changePw.help": "Bevor es losgeht, lege bitte ein eigenes Passwort fest.",

  "changePw.current": "Aktuelles Passwort",

  "changePw.new": "Neues Passwort",

  "changePw.confirm": "Neues Passwort bestätigen",

  "changePw.save": "Passwort speichern",

  "changePw.saving": "Speichern…",

  "changePw.required": "Bitte fülle alle Felder aus.",

  "changePw.mismatch": "Die neuen Passwörter stimmen nicht überein.",

  "changePw.sameAsCurrent": "Das neue Passwort muss sich vom aktuellen unterscheiden.",

  "changePw.wrongCurrent": "Das aktuelle Passwort ist falsch.",

  "changePw.failed": "Passwort konnte nicht geändert werden.",

  "changePw.rewrapFailed":
    "Dein Passwort wurde geändert, aber dein Verschlüsselungs-Schlüssel konnte nicht übernommen werden. Stelle ihn mit deinem Wiederherstellungs-Code wieder her oder füge ihn erneut hinzu.",

  "changePw.e2eeNote":
    "Hinweis: Dabei wird auch dein Verschlüsselungs-Schlüssel neu gesichert.",

};

/** English. */
const en: Dict = {
  "boot.loading": "Loading…",

  "login.title": "wiederdabei",

  "login.subtitle": "Sign in to join your class.",

  "login.username": "Username",

  "login.password": "Password",

  "login.signIn": "Sign in",

  "login.signingIn": "Signing in…",

  "login.rememberMe": "Stay signed in",

  "login.rememberMeHint": "Only use this on your own device.",

  "login.error.required": "Username and password required.",

  "login.error.invalid": "Wrong username or password.",

  "login.error.failed": "Login failed.",

  "login.forgot": "Forgot password?",

  "login.notVerified": "Your email isn't verified yet.",

  "login.resend": "Resend verification email",

  "login.resending": "Sending…",

  "login.resent": "If needed, a new email is on its way.",

  // Concurrent-session limit (HTTP 409 session_limit)
  "login.sessionLimit.title": "Too many active logins",

  "login.sessionLimit.intro":
    "You can be signed in on at most {max} device(s) at once. Pick one or more existing logins to end so you can continue here.",

  "login.sessionLimit.selectHint":
    "Select at least {count} session(s) to free a slot.",

  "login.sessionLimit.activeFor": "active for {age}",

  "login.sessionLimit.justNow": "just now",

  "login.sessionLimit.unknownAge": "unknown",

  "login.sessionLimit.unknownDevice": "unknown device",

  "login.sessionLimit.unknownBrowser": "unknown browser",

  "login.sessionLimit.unknownIp": "unknown IP",

  "login.sessionLimit.killContinue": "End selected & continue",

  "login.sessionLimit.cancel": "Cancel",

  "account.back": "← Back to sign in",

  "reset.title": "Forgot password",

  "reset.help": "Enter your email. We'll send you a link.",

  "reset.email": "Email",

  "reset.send": "Send link",

  "reset.sending": "Sending…",

  "reset.sent": "If an account exists, a link is on its way. Check your inbox.",

  "reset.newTitle": "Set a new password",

  "reset.newPassword": "New password",

  "reset.confirm": "Confirm password",

  "reset.save": "Save password",

  "reset.saving": "Saving…",

  "reset.done": "Your password has been reset. Redirecting…",

  "reset.mismatch": "Passwords do not match.",

  "reset.invalid": "This link is invalid or has expired.",

  "reset.e2eeWarn":
    "Warning: this reset CANNOT recover your existing encryption key (your old password is unknown). You'll need your recovery code — otherwise you'll get a fresh key and must add it again.",

  "reset.recoveryTitle": "Restore encryption (optional)",

  "reset.recoveryInfo":
    "Have a recovery code? Enter it to rescue your encryption key.",

  "reset.recoveryCode": "Recovery code",

  "reset.recoverySubmit": "Restore key",

  "reset.recoveryOk": "Encryption restored.",

  "reset.recoveryFail": "Restore failed. Check the code.",

  "verify.title": "Email verification",

  "verify.loading": "Verifying your email…",

  "verify.ok": "Your email is verified. You can now sign in.",

  "verify.fail": "This link is invalid or has expired.",

  "class.logout": "Logout",

  "class.leaveRoom": "Leave room",

  // Class — mobile header hamburger (collapses the secondary header actions)
  "class.menu.open": "Open more actions",

  "class.menu.close": "Close more actions",

  // Class — clean-view toggle (hover tooltips)
  "cleanView.show.tip": "Show the controls again.",

  "cleanView.hide.tip": "Hide all controls – only the stream stays.",

  // Room selector (front-door gate)
  "room.finding": "Finding your classrooms…",

  "room.pick": "Pick a classroom",

  "room.none": "No classroom is online right now.",

  "room.retry": "Retry",

  "room.visibility.public": "Public",

  "room.visibility.restricted": "Restricted",

  // E2EE settings popup (opened from the HUD lock)
  "e2ee.settings.title": "Encryption",

  "e2ee.settings.statusEncrypted": "Your stream is encrypted.",

  "e2ee.settings.statusPlain": "Your stream is not encrypted.",

  "e2ee.settings.statusForced":
    "Encryption is required by the classroom device.",

  "e2ee.settings.statusRekey":
    "The encryption key changed — re-scan the QR code.",

  "e2ee.settings.statusUnavailable":
    "Encryption is not supported by this browser.",

  "e2ee.settings.enable": "Turn encryption on",

  "e2ee.settings.enabling": "Enabling encryption…",
  "e2ee.settings.enabledToast": "Connection is now encrypted",

  "e2ee.settings.disable": "Turn encryption off",

  "e2ee.settings.scan": "Scan QR",

  "e2ee.settings.repair": "Repair encryption",

  "e2ee.settings.close": "Close",

  "status.connected": "Connected",

  "status.connecting": "Connecting…",

  "status.disconnected": "Disconnected",

  "status.error": "Error",

  "raisehand.stop": "Lower hand",

  "clock.label": "Current time",

  "vu.label": "Classroom audio level",

  "camera.label": "Choose camera",

  "selfPreview.label": "Your camera (live to classroom)",

  "selfPreview.tag": "Live",

  "adminMsg.dismiss": "Dismiss message",

  "device.offline.title": "Device is not online",

  "device.offline.lastOnline": "Last online",

  "device.offline.offlineFor": "Offline for",

  "device.offline.wasOnline": "Was online for",

  "mode.goVisible": "Go visible",

  "mode.goInvisible": "Go invisible",

  "raisehand.start": "Raise hand",

  "audio.on": "Mic on",

  "audio.off": "Mic off",

  "video.on": "Cam on",

  "video.off": "Cam off",

  "sound.on": "Sound on",

  "sound.off": "Sound off",

  "text.open": "Text",

  "mode.goInvisible.tip": "Nobody in class can see or hear you anymore.",

  "mode.goVisible.tip": "Your class can see and hear you again.",

  "raisehand.start.tip": "Raise your hand – the teacher sees you want to speak.",

  "raisehand.stop.tip": "Take your raised hand back down.",

  "audio.on.tip": "Turn your mic on so the class can hear you.",

  "audio.off.tip": "Turn your mic off – nobody can hear you.",

  "video.on.tip": "Turn your camera on so the class can see you.",

  "video.off.tip": "Turn your camera off – nobody can see you.",

  "sound.on.tip": "Unmute the class – you can hear them.",

  "sound.off.tip": "Mute the class – you won't hear them.",

  "text.open.tip": "Write a message to the class.",

  "text.placeholder": "Type a message…",

  "text.send": "Send",

  "text.close": "Close",

  "video.confirm.title": "Send camera to classroom?",

  "video.confirm.starting": "Starting camera…",

  "video.confirm.preview": "This is what your classroom will see.",

  "video.confirm.cancel": "Cancel",

  "video.confirm.send": "Send to classroom",

  "error.sessionExpired": "Session expired. Please sign in again.",

  "error.joinFailed": "Could not join class.",

  "error.connect.roomOffline":
    "Your class is not online right now. Wait a moment and try again — or ask your teacher whether the classroom device is switched on.",

  "error.connect.sessionLimit":
    "Too many devices are in this class right now. Close the app on your other devices and try again.",

  "error.connect.serverBusy":
    "The class is busy right now. Wait a moment and try again.",

  "error.connect.serverError":
    "The server is having a problem. Please try again in a few minutes.",

  "error.connect.signalUnreachable":
    "The server cannot be reached. Check your internet connection and try again.",

  "error.connect.authExpired": "Your session expired. Please sign in again.",

  "error.connect.e2eeFailed":
    "Encryption could not be set up. Scan the classroom QR code again, or repair encryption in the settings.",

  "error.connect.iceFailed":
    "Your network is blocking the connection. Try a different Wi-Fi or your mobile data.",

  "error.connect.turnUnreachable":
    "No media connection could be established. Try a different Wi-Fi or your mobile data — school and company networks often block it.",

  "error.connect.networkOffline":
    "You are offline. Check your Wi-Fi or mobile data and try again.",

  "error.connect.unknown":
    "The connection failed. Try again — if it keeps failing, give the code below to support.",

  "error.connect.short.server_error": "Server problem",

  "error.connect.short.auth_expired": "Signed out",

  "error.connect.short.e2ee_failed": "Encryption",

  "error.connect.short.ice_failed": "Network blocked",

  "error.connect.short.turn_unreachable": "Network blocked",

  "error.connect.short.network_offline": "Offline",

  "error.connect.short.unknown": "Error",

  "error.connect.code": "Code: {code}",

  "error.connect.detailsShow": "Technical details",

  "error.cameraBlocked": "Camera blocked",

  "error.mic": "Mic: {reason}",

  "error.cam": "Cam: {reason}",

  "error.blocked": "blocked",

  "install.title": "Add to your phone",

  "install.step1": "1. Tap the Share button (the square with an up-arrow).",

  "install.step2": "2. Choose “Add to Home Screen”.",

  "install.gotIt": "Got it",

  "install.dismiss": "Close",

  "profile.title": "Your profile picture",

  "profile.help": "Pick a photo. Your class sees it when your camera is off.",

  "profile.choose": "Choose photo",

  "profile.retake": "Pick another",

  "profile.save": "Save",

  "profile.saving": "Saving…",

  "profile.skip": "Later",

  "profile.error": "Couldn't save the picture. Please try again.",

  "profile.tooLarge": "That image is too large. Pick a smaller photo.",

  "profile.placeholderAlt": "Your profile picture",

  "profile.takePhoto": "Take photo",

  "webcam.title": "Take a photo",

  "webcam.snapshot": "Take snapshot",

  "webcam.use": "Use photo",

  "webcam.retry": "Retry",

  "webcam.cancel": "Cancel",

  "webcam.error": "Camera unavailable. Pick a photo instead.",

  "viewers.label": "Who's watching",

  // E2EE (Phase 5 — Scaffolding)
  "e2ee.status.encrypted": "Encrypted",

  "e2ee.status.partial": "Partially encrypted",

  "e2ee.status.unencrypted": "Not encrypted",

  "e2ee.scan.title": "Scan device key",

  "e2ee.qr.scanRequired":
    "QR scan required — scan the code on the classroom device to join.",

  "e2ee.scan.help": "Scan the QR code on the classroom device to store its key.",

  "e2ee.scan.button": "Scan key",

  "e2ee.scan.start": "Start camera",

  "e2ee.scan.stop": "Stop",

  "e2ee.scan.close": "Close",

  "e2ee.scan.cameraError": "Could not start the camera.",

  "e2ee.scan.error": "Could not process the QR code. Please try again.",

  "e2ee.scan.invalid": "Invalid QR code.",

  "e2ee.scan.stored": "Key stored.",

  "e2ee.scan.storedNotLive": "Key stored, but live decryption could not be enabled.",

  "e2ee.scan.storeError": "Could not store the key.",

  "e2ee.scan.expired": "QR code has expired. Please regenerate it.",

  "e2ee.scan.badSig": "Invalid signature — QR code is not trusted.",

  "e2ee.scan.noIdentity":
    "Encryption is not set up yet. Please sign in again.",

  // E2EE key provisioning from the Profile page (Part B: scan / paste)
  "e2ee.manual.title": "Add a key",
  "e2ee.manual.info":
    "Scan the classroom device's QR code, or paste the key as text, to add it to your account.",
  "e2ee.manual.addKey": "Add a key",
  "e2ee.manual.pasteLabel": "Paste a key",
  "e2ee.manual.placeholder": "Paste QR contents or a key here…",
  "e2ee.manual.submit": "Save key",
  "e2ee.manual.label": "Enter the code from the classroom device",
  "e2ee.manual.or": "or enter a code manually",
  "e2ee.manual.toggle": "Enter a code instead of scanning",
  "e2ee.manual.scanToggle": "Scan a QR code instead",

  // E2EE overlay lock (Phase 5) — click to toggle / locked / unavailable states
  "e2ee.overlay.encryptedToggle": "Encrypted — tap to turn encryption off.",
  "e2ee.overlay.plainToggle": "Not encrypted — tap to turn encryption on.",
  "e2ee.overlay.forced": "Encryption is required by the classroom device.",
  "e2ee.overlay.unavailable":
    "Encryption is not supported by this browser.",
  // Key rotation (Phase 6): the active session key changed → re-scan needed.
  "e2ee.overlay.rekey":
    "The encryption key changed — re-scan the QR code.",
  "e2ee.rekey.title": "Encryption key changed",
  "e2ee.rekey.detail":
    "The classroom device is using a new key. Re-scan the QR code to keep the stream encrypted.",
  "e2ee.help.button": "Help",
  "e2ee.help.title": "Encryption — help",
  "e2ee.help.current": "Your situation right now:",
  "e2ee.help.intro":
    "With end-to-end encryption, video and audio are encrypted on the classroom device and only decrypted on your device. The server sees only encrypted data and cannot watch or listen in.",
  "e2ee.help.status.title": "The lock icon",
  "e2ee.help.status.body":
    "Green = encrypted, yellow = not encrypted, amber = partially encrypted.",
  "e2ee.help.optional.title": "Encryption is optional",
  "e2ee.help.optional.body":
    "You can turn encryption on or off yourself using the lock icon.",
  "e2ee.help.forced.title": "Encryption is required",
  "e2ee.help.forced.body":
    "The classroom device requires encryption, so you can't turn it off. If you are asked to, scan the QR code on the classroom device.",
  "e2ee.help.qr.title": "QR scan required",
  "e2ee.help.qr.body":
    "Automatic key sharing is turned off. You must take the key in person: scan the QR code shown on the classroom device.",
  "e2ee.help.rekey.title": "The key changed",
  "e2ee.help.rekey.body":
    "The classroom device is using a new key. Tap \"Scan key\" and re-scan the current QR code on the classroom device.",
  "e2ee.help.unsupported.title": "This browser doesn't support encryption",
  "e2ee.help.unsupported.body":
    "Use a recent Chrome or Chromium browser on desktop. iPhone and iPad are not supported.",
  "e2ee.help.blocked.title": "Join blocked",
  "e2ee.help.blocked.body":
    "This room requires encryption but your browser doesn't support it. Switch to a recent desktop Chrome and try again.",
  "e2ee.help.close": "Close",
  // Blocked-join remediation screen (forced E2EE + incapable client)
  "e2ee.blocked.title": "Encryption required",
  "e2ee.blocked.detail":
    "This classroom requires end-to-end encryption, but your browser does not support it, so the session cannot start.",
  "e2ee.blocked.step.chrome":
    "Use a recent Chrome or Chromium desktop browser.",
  "e2ee.blocked.step.ios": "iOS devices (iPhone/iPad) are not supported.",
  "e2ee.blocked.step.update":
    "Update your browser to the latest version and try again.",
  "e2ee.blocked.retry": "Try again",
  // First-run scan wording (P1): distinct from rotation ("key changed").
  "e2ee.scan.firstRun.title": "Enable encryption",
  "e2ee.scan.firstRun.body":
    "Scan the QR code on the class display to start end-to-end encryption.",

  // Recovery-code routing (Phase 1): login could not unlock the stored identity
  // but a recovery blob exists — a QR scan can't help, so ask for the code.
  "e2ee.recovery.title": "Restore encryption",
  "e2ee.recovery.detail":
    "Your encryption key couldn't be unlocked after a password change. Enter your recovery code to regain access.",
  "e2ee.recovery.placeholder": "Recovery code",
  "e2ee.recovery.submit": "Restore",
  "e2ee.recovery.ok": "Encryption restored.",
  "e2ee.recovery.fail": "Restore failed. Check the code.",

  // Compatibility downgrade (Phase 3): the whole room runs in plaintext because
  // a participant's device cannot encrypt.
  "e2ee.compat.plaintextTitle": "Encryption OFF",
  "e2ee.compat.plaintextDetail":
    "A participant's device can't encrypt — this session is running unencrypted.",
  // User-controlled / automatic plaintext fallback (compat downgrade).
  "e2ee.compat.continuePlain": "Continue unencrypted",
  // Reliability: identity not ready yet (login provisioning failed/timed out).
  "e2ee.preparing": "Preparing encryption…",
  "e2ee.notReady.title": "Encryption not ready yet",
  "e2ee.notReady.detail":
    "Your encryption key isn't available yet. Please wait a moment — or continue unencrypted.",
  // FIX B: bounded notReady auto-recovery — live countdown + terminal + manual.
  "e2ee.notReady.recovering":
    "Setting up encryption … {seconds}s left. You don't need to do anything.",
  "e2ee.notReady.terminal":
    "Encryption couldn't be set up automatically. Tap “Renew key” or log in again.",
  "e2ee.notReady.reprovision": "Renew key / Reconnect",
  // Keyless recovery UX: key from the classroom device hasn't arrived yet.
  "e2ee.keyMissing.title": "Setting up encryption",
  "e2ee.keyMissing.detail":
    "Your key from the classroom device hasn't arrived yet. The video stays encrypted until the key is here.",
  "e2ee.keyMissing.waiting":
    "Waiting for the key from the classroom device … {seconds}s left",
  "e2ee.keyMissing.waitingHint":
    "This can take up to a minute. You don't need to do anything.",
  "e2ee.keyMissing.repair": "Repair encryption",
  "e2ee.keyMissing.relogin": "Log in again to repair encryption",
  "e2ee.keyMissing.reloginDetail":
    "Log in again quickly. You'll be sent right back to the classroom afterwards.",

  "paused.title": "Your teacher paused the stream and will continue soon.",

  "video.confirm.camera": "Camera",

  "testMode.badge": "Test mode",

  "testMode.tip": "You are in test mode. This is a demo — nothing is recorded.",

  "demo.signingIn": "Starting demo…",

  "demo.failed": "Could not start the demo.",

  "demo.placeholder": "Sample stream (test mode)",

  "demo.classScreen.title": "Classroom view · what the class sees",
  "demo.classScreen.handUp": "{name} has a question",
  "demo.classScreen.you": "You",

  // Profile page
  "profilePage.open": "Open profile",
  "profilePage.back": "← Back to class",
  "profilePage.title": "Your profile",
  "profilePage.saved": "Saved.",
  "profilePage.saveError": "Could not save. Please try again.",
  "profilePage.required": "Please fill in all fields.",
  "profilePage.wrongPassword": "That password is wrong.",
  "profilePage.currentPassword": "Current password",

  "profilePage.photo.title": "Profile photo",
  "profilePage.photo.remove": "Remove photo",
  "profilePage.photoRemoved": "Photo removed.",

  "profilePage.identity.title": "Name & nickname",
  "profilePage.identity.fullName": "Full name",
  "profilePage.identity.nickname": "Nickname",
  "profilePage.identity.nicknameHint":
    "Your nickname is shown instead of your email.",
  "profilePage.identity.language": "Language",

  "profilePage.email.title": "Email address",
  "profilePage.email.current": "Current",
  "profilePage.email.new": "New email",
  "profilePage.email.submit": "Change email",
  "profilePage.email.sent":
    "We sent a confirmation link to the new address.",
  "profilePage.email.pending":
    "Pending confirmation: {email}. Check your inbox.",
  "profilePage.email.taken": "That email is already in use.",
  "profilePage.email.invalid": "That email address is invalid.",

  "profilePage.password.title": "Password",
  "profilePage.password.new": "New password",
  "profilePage.password.confirm": "Confirm new password",
  "profilePage.password.submit": "Change password",
  "profilePage.password.changed": "Your password was changed.",
  "profilePage.password.changedRewrapFailed":
    "Password changed, but your encryption key could not be carried over. Restore it below with your recovery code.",
  "profilePage.password.needRecovery":
    "This password change would destroy your encryption key. Create a recovery code below first.",
  "profilePage.password.confirmTitle": "Change your password?",
  "profilePage.password.confirmBody":
    "Changing your password re-secures your encryption key. If anything goes wrong you'll need your recovery key.",
  "profilePage.password.confirmNoRecovery":
    "You have no recovery code. If anything goes wrong, your key cannot be restored.",
  "profilePage.password.confirmCancel": "Cancel",
  "profilePage.password.confirmProceed": "Change password",
  "profilePage.password.mismatch": "The passwords don't match.",
  "profilePage.password.weak": "The password must be at least 8 characters.",

  "profilePage.e2ee.title": "Encryption",
  "profilePage.e2ee.info":
    "End-to-end encryption protects your stream. Only you and your class can see it.",
  "profilePage.e2ee.identity": "Your key",
  "profilePage.e2ee.noIdentity": "Not set up yet",
  "profilePage.e2ee.recoveryNeeded":
    "Your encryption couldn't be unlocked after a password change. Restore it with your recovery code.",
  "profilePage.e2ee.rewrapError":
    "Your encryption key could not be carried over during the last password change. Restore it with your recovery code, or add the key again.",
  "profilePage.e2ee.recoveryTitle": "Recovery code",
  "profilePage.e2ee.recoveryInfo":
    "A recovery code lets you regain access to your encryption after a password change.",
  "profilePage.e2ee.recoveryWarn":
    "Write this code down and keep it safe. It is shown only once.",
  "profilePage.e2ee.recoverySetup": "Create recovery code",
  "profilePage.e2ee.recoveryRotate": "Create new code",
  "profilePage.e2ee.recoveryDisable": "Disable recovery",
  "profilePage.e2ee.recoverySaved": "Recovery code created.",
  "profilePage.e2ee.recoveryDisabled": "Recovery disabled.",
  "profilePage.e2ee.recoveryError": "Could not set up recovery.",
  "profilePage.e2ee.restoreTitle": "Restore encryption",
  "profilePage.e2ee.restoreInfo":
    "Enter your recovery code and current password to restore your encryption.",
  "profilePage.e2ee.restoreCode": "Recovery code",
  "profilePage.e2ee.restoreSubmit": "Restore",
  "profilePage.e2ee.restored": "Encryption restored.",
  "profilePage.e2ee.restoreFailed":
    "Restore failed. Check the code and password.",

  "profilePage.teacher.title": "Teacher contact",
  "profilePage.teacher.info": "Your teacher's email address.",
  "profilePage.teacher.forRoom": "Teacher for \u201C{room}\u201D.",
  "profilePage.teacher.none": "No teacher contact address available.",

  "confirmEmail.title": "Confirm email change",
  "confirmEmail.loading": "Confirming your new email…",
  "confirmEmail.ok": "Your email address was changed.",
  "confirmEmail.fail": "This link is invalid or has expired.",

  // Forced first-login password change
  "changePw.title": "Set a new password",

  "changePw.help": "Before you start, please set your own password.",

  "changePw.current": "Current password",

  "changePw.new": "New password",

  "changePw.confirm": "Confirm new password",

  "changePw.save": "Save password",

  "changePw.saving": "Saving…",

  "changePw.required": "Please fill in all fields.",

  "changePw.mismatch": "The new passwords do not match.",

  "changePw.sameAsCurrent": "The new password must differ from the current one.",

  "changePw.wrongCurrent": "The current password is wrong.",

  "changePw.failed": "Could not change the password.",

  "changePw.rewrapFailed":
    "Your password was changed, but your encryption key could not be carried over. Restore it with your recovery code, or add it again.",

  "changePw.e2eeNote":
    "Note: this also re-secures your encryption key.",

};

const DICTS: Record<Locale, Dict> = { de, en };

/** Active locale store. Default DE. */
export const locale: Writable<Locale> = writable(DEFAULT_LOCALE);

/** Narrow an arbitrary value to a supported Locale, falling back to DE. */
export function normalizeLocale(value: unknown): Locale {
  return SUPPORTED.includes(value as Locale) ? (value as Locale) : DEFAULT_LOCALE;
}

/** Set the active locale, validating and falling back to DE on bad input. */
export function setLocale(value: unknown): void {
  const next = normalizeLocale(value);
  locale.set(next);
  if (typeof document !== "undefined") {
    document.documentElement.lang = next;
  }
}

function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_m, key: string) =>
    key in params ? String(params[key]) : `{${key}}`,
  );
}

/** Resolve a key for an explicit locale (DE fallback, then the key itself). */
export function translate(
  loc: Locale,
  key: string,
  params?: Record<string, string | number>,
): string {
  const dict = DICTS[loc] ?? DICTS[DEFAULT_LOCALE];
  const raw = dict[key] ?? DICTS[DEFAULT_LOCALE][key] ?? key;
  return interpolate(raw, params);
}

/**
 * Imperative translate against the current store value. Handy outside Svelte
 * reactive contexts (e.g. building an error string in a handler).
 */
export function t(key: string, params?: Record<string, string | number>): string {
  return translate(get(locale), key, params);
}

/**
 * Reactive translator store. In a component: `$_("login.title")`. Re-evaluates
 * whenever `locale` changes so the visible text switches with the locale.
 */
export const _: Readable<(key: string, params?: Record<string, string | number>) => string> =
  derived(locale, ($locale) => (key: string, params?: Record<string, string | number>) =>
    translate($locale, key, params),
  );

export const __test = { de, en, SUPPORTED };
